import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";

import {
  ArrowRight20Px,
  CircularProgressWithLabel,
  mobileWidth
} from "@qubit/autoparts";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { useFlag } from "~/config/flags";
import { setSidePanelOrderId } from "~/features/batches/adminBatches.slice";
import { formatTime } from "~/lib/helpers";

import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";

import { selectBatches } from "./batchCards.slice";

export const BatchOrderSummaries = ({ batchId }: { batchId: Guid }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const flags = useFlag();
  const isAdminBatchesRoute = pathname.includes("/admin-batches");
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const batch = useAppSelector(selectBatches).find(
    (b) => b.batchId === batchId
  )!;
  const isMobile = useMediaQuery(mobileWidth);
  const fulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const { orderSummaries } = batch;
  const columnTemplate = isMobile ? "1fr 1fr" : "50px 1fr 50px 1fr 50px 1fr ";

  return (
    <Box
      display="grid"
      gridTemplateColumns={columnTemplate}
      alignItems="center"
      gap={1}
      mt={1}
    >
      {orderSummaries.map((orderSummary, i) => {
        const rowSx = {
          gridColumn: isMobile ? "span 1" : "span 2",
          width: "100%"
        };
        const rowContent = (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ width: "100%" }}
          >
            {!isMobile && (
              <CircularProgressWithLabel
                aria-label={`order-${i}-circular-progress`}
                totalCount={orderSummary.orderPicks}
                completeCount={orderSummary.orderCompletedPicks}
                cancelledCount={orderSummary.orderCanceledPicks}
                size={24}
              />
            )}
            <Stack direction="row" sx={{ flex: 1 }}>
              <Typography variant={isAdminBatchesRoute ? "body1" : "caption"}>
                {`${orderSummary.firstName} ${orderSummary.lastName} ${formatTime(
                  orderSummary.pickingEndTime,
                  fulfillmentCenter?.timeZone
                )}`}
              </Typography>
              {orderSummary.externalOrderId && (
                <Typography
                  variant={isAdminBatchesRoute ? "body1" : "caption"}
                  component="span"
                  sx={{ color: "darkGray.main" }}
                >
                  {` ${orderSummary.externalOrderId}`}
                </Typography>
              )}
            </Stack>
            {isAdminBatchesRoute && (
              <ArrowRight20Px
                aria-hidden
                fill={theme.palette.text.secondary}
                style={{
                  width: 18,
                  height: 18
                }}
              />
            )}
          </Stack>
        );

        if (isAdminBatchesRoute) {
          return (
            <Button
              key={orderSummary.orderId}
              variant="subtle"
              component="span"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (flags.isNewOrdersPageEnabled)
                  dispatch(setSidePanelOrderId(orderSummary.orderId));
              }}
              aria-label={t("view order")}
              sx={rowSx}
            >
              {rowContent}
            </Button>
          );
        }

        return (
          <Box
            key={orderSummary.orderId}
            component="span"
            display="block"
            sx={rowSx}
          >
            {rowContent}
          </Box>
        );
      })}
    </Box>
  );
};
